import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  inject,
  Injector,
  ChangeDetectionStrategy
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { User } from 'src/app/models/user';
import { Organization } from 'src/app/models/organization.model';
import { AuthService } from 'src/app/services/auth.service';
import { SettingsService } from 'src/app/services/settings.service';
import { MenuSettings } from 'src/app/models/menu-settings';
import { AppsSettings } from 'src/app/models/apps-settings';
import { MatToolbar } from '@angular/material/toolbar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIcon } from '@angular/material/icon';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { UserStateService } from 'src/app/services/signals/user-state.service';
import { ThemesService } from 'src/app/services/themes.service';
import { runInInjectionContext, effect } from '@angular/core';
import { GumOrganizationsService } from 'src/app/services/gumservices/gum-organizations.service';
import { MenuSettingsComponent } from '../menu-settings/menu-settings.component';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';

@Component({
  selector: 'app-nav-top-bar',
  templateUrl: './nav-top-bar.component.html',
  styleUrls: ['./nav-top-bar.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatToolbar,
    MatIcon,
    RouterLink,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    FaIconComponent,
    MenuSettingsComponent,
    MatInputModule,
    MatOptionModule,
  ],
})
export class NavTopBarComponent implements OnInit {
  faFilter = faFilter;

  isDarkMode = false;
  currentUser: User | null = null;
  currentOrganization: Organization | null = null;
  organizationsFilter: Organization[] = [];
  isEditable = false;

  menuSettings?: MenuSettings;
  cesarApplications: AppsSettings[] = [];

  searchControl!: FormGroup;
  @ViewChild('searchInput') searchInput!: ElementRef;

  private userStateService = inject(UserStateService);
  private injector = inject(Injector);

  constructor(
    private themesService: ThemesService,
    private router: Router,
    private authService: AuthService,
    private settingsService: SettingsService,
    private organizationsService: GumOrganizationsService,
    private fb: FormBuilder
  ) {
    runInInjectionContext(this.injector, () => {
      effect(() => {
        const user = this.userStateService.getCurrentUserSignal()();
        if (user) this.currentUser = user;
      });
    });
  }

  ngOnInit(): void {
    const userFromSession = sessionStorage.getItem('user');
    if (userFromSession) {
      const parsedUser = JSON.parse(userFromSession);
      this.userStateService.setCurrentUser(parsedUser);
    }

    const savedMenuSettings = sessionStorage.getItem('menuSettings');
    if (savedMenuSettings) {
      this.menuSettings = JSON.parse(savedMenuSettings);
      this.cesarApplications = Object.values(this.menuSettings?.apps);
    } else {
      this.settingsService.getSettings().subscribe((menuSettings) => {
        this.menuSettings = menuSettings;
        this.cesarApplications = Object.values(menuSettings.apps);
        sessionStorage.setItem('menuSettings', JSON.stringify(menuSettings));
      });
    }

    this.currentOrganization =
      this.organizationsService.getCurrentOrganization();

    this.searchControl = this.fb.group({
      organization: [this.currentOrganization?.display ?? ''],
      organizationControl: [''],
    });

    this.searchControl
      .get('organizationControl')!
      .valueChanges.subscribe((val) => {
        const search = typeof val === 'string' ? val : val?.display ?? '';
        this.searchOrganization(search);
      });

    this.organizationsService.currentOrganizationChange$.subscribe((newOrg) => {
      this.currentOrganization = newOrg;
      this.searchControl.get('organization')?.setValue(newOrg?.display);
    });

    this.themesService.currentTheme$.subscribe((theme) => {
      this.isDarkMode = theme.includes('dark');
    });
  }

  get fullName(): string {
    if (!this.currentUser?.display) return '';

    const parts = this.currentUser.display.split(' ');
    if (parts.length >= 2) {
      const lastname = parts[0];
      const firstname = parts.slice(1).join(' ');
      return `${firstname} ${lastname}`;
    }

    return this.currentUser.display;
  }

  searchOrganization(value: string) {
    const search = value.toLowerCase();

    this.organizationsService.getOrganisations(false, '').subscribe((orgs) => {
      this.organizationsFilter = orgs.filter((o) =>
        o.display?.toLowerCase().includes(search)
      );
    });
  }

  selectOrganization(organization: Organization) {
    this.organizationsService.setCurrentOrganization(organization);

    // Met à jour manuellement le champ affiché
    this.searchControl.get('organization')?.setValue(organization.display);

    this.router.navigate(['/dashboard']);
    this.isEditable = false;
  }

  onEdit() {
    this.isEditable = true;
    setTimeout(() => this.searchInput?.nativeElement.focus(), 100);
  }

  focus() {
    this.searchControl.get('organizationControl')?.setValue('');
  }

  blur() {
    // On retarde pour laisser Angular mettre à jour currentOrganization
    setTimeout(() => {
      const current = this.organizationsService.getCurrentOrganization();
      this.searchControl.get('organization')?.setValue(current?.display ?? '');
      this.isEditable = false;
    }, 150);
  }

  shouldDisplayNavbar(): boolean {
    return this.router.url !== '/login';
  }

  displayFn(org: Organization): string {
    return org?.display ?? '';
  }

  trackById(index: number, item: Organization): string {
    return item.id;
  }

  onThemeChange() {
    this.isDarkMode = !this.isDarkMode;
    const newTheme = this.isDarkMode ? 'avm-dark-theme' : 'avm-light-theme';
    this.themesService.changeTheme(newTheme);
  }

  logout() {
    if (this.currentUser?.account) {
      this.authService.logout(this.currentUser.account);
      this.userStateService.setCurrentUser(null);
      sessionStorage.removeItem('user');
      this.router.navigate(['/login']);
    }
  }
}
